import { apiClient } from '@api/client';
import type { IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Typography,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Divider,
  Tooltip
} from 'antd';
import React, { Fragment, useState } from 'react';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import { LtiClientRoleDto } from '@api/Api';
import { nonNull } from '@helpers/non-null';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useOrganization } from '@components/OrganizationBoundary';

const { Title, Paragraph, Link } = Typography;

interface RoleMapping {
  ltiRole: string;
  role: 'organization://admin' | LtiClientRoleDto['workspaceRole'];
}

const DEFAULT_ROLE_MAPPINGS: RoleMapping[] = [
  { ltiRole: 'Administrator', role: 'organization://admin' },
  { ltiRole: 'Instructor', role: 'workspace://team-lead' },
  { ltiRole: 'Learner', role: 'workspace://contributor' }
];

type ViewState =
  | {
      type: 'mapping';
    }
  | {
      type: 'instructions';
      integrationId: string;
      url: string;
    }
  | {
      type: 'result';
      integrationId: string;
      url: string;
    };

export default function LtiInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const createRegistrationUrl = useMutation({
    mutationFn: async ({
      trustEmail,
      roles
    }: {
      trustEmail: boolean;
      roles: RoleMapping[];
    }) => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerCreateRegistrationUrl(
          {
            organizationId,
            trustEmail,
            roles: roles.map((x) => ({
              ltiRole: x.ltiRole,
              organizationRole:
                x.role === 'organization://admin'
                  ? x.role
                  : 'organization://workspace-member',
              workspaceRole:
                x.role === 'organization://admin'
                  ? 'workspace://team-lead'
                  : x.role
            }))
          }
        );
      return data;
    }
  });

  const [view, setView] = useState<ViewState>({ type: 'mapping' });

  const resultIntegrationId =
    view.type === 'result' ? view.integrationId : undefined;
  const {
    data: registrationInfo,
    isLoading: resultIsLoading,
    refetch
  } = useQuery({
    enabled: !!resultIntegrationId,
    queryKey: ['lti', 'registration-info', resultIntegrationId],
    queryFn: async () => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerRegistrationInfo({
          integrationId: nonNull(resultIntegrationId)
        });
      return data;
    },
    refetchOnWindowFocus: false
  });

  const [roleMappingForm] = Form.useForm<{
    roles: RoleMapping[];
    trustEmail: boolean;
  }>();

  const { currentOrganization } = useOrganization(true);
  const canTrustEmail = !!currentOrganization.entity?.ownDomains?.length;

  if (view.type === 'mapping') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Title level={3}>Choose How LMS Users Join StreamWork</Title>
        <Form
          form={roleMappingForm}
          layout="vertical"
          initialValues={{
            trustEmail: canTrustEmail,
            roles: DEFAULT_ROLE_MAPPINGS
          }}
          style={{ marginBottom: 16 }}
          onFinish={async (values) => {
            const { integrationId, url } =
              await createRegistrationUrl.mutateAsync(values);
            setView({
              type: 'instructions',
              integrationId,
              url
            });
          }}
        >
          <Form.Item
            label="Trust institution email addresses"
            extra={
              <div style={{ maxWidth: '60ch' }}>
                When enabled, StreamWork will trust email addresses provided by
                the LMS for user identification. This allows new users coming
                from the LMS to skip the email verification step.
              </div>
            }
          >
            <Tooltip
              overlayClassName="toggle_favorite_overlay"
              placement="right"
              title={
                canTrustEmail ? null : (
                  <>
                    Please contact
                    <br />
                    <a href="mailto:support@streamwork.com">
                      support@streamwork.com
                    </a>
                    <br />
                    to enable this feature
                  </>
                )
              }
            >
              <span>
                <Form.Item noStyle name="trustEmail" valuePropName="checked">
                  <Switch disabled={!canTrustEmail} />
                </Form.Item>
              </span>
            </Tooltip>
          </Form.Item>
          <Divider />
          <Paragraph style={{ maxWidth: '60ch' }}>
            Specify how incoming <strong>LTI roles</strong> should be translated
            into <strong>StreamWork roles</strong>. Default suggestions are
            provided, but you can add, edit, or remove mappings below.
          </Paragraph>
          <Form.List name="roles">
            {(fields, { add, remove }) => (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  columnGap: 16
                }}
              >
                {fields.map((field) => (
                  <Fragment key={field.key}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'ltiRole']}
                      rules={[
                        { required: true, message: 'LTI role is required' }
                      ]}
                    >
                      <Input placeholder="LTI role (e.g., Learner)" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'role']}
                      rules={[
                        {
                          required: true,
                          message: 'StreamWork role is required'
                        }
                      ]}
                    >
                      <Select
                        placeholder="StreamWork role"
                        options={[
                          { value: 'organization://admin', label: 'Admin' },
                          {
                            value: 'workspace://team-lead',
                            label: 'Workspace lead'
                          },
                          {
                            value: 'workspace://member',
                            label: 'Workspace member'
                          },
                          {
                            value: 'workspace://contributor',
                            label: 'Contributor'
                          }
                        ]}
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    )}
                  </Fragment>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ ltiRole: '', swRole: '' })}
                  icon={<PlusOutlined />}
                  style={{
                    gridColumnStart: 1,
                    gridColumnEnd: -2,
                    marginBottom: 16
                  }}
                >
                  Add mapping
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
        <div
          style={{
            display: 'flex',
            marginTop: 'auto',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            type="primary"
            loading={createRegistrationUrl.isLoading}
            onClick={() => roleMappingForm.submit()}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }
  if (view.type === 'result' && !resultIsLoading) {
    const clientId = registrationInfo?.clientId;
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Title level={3}>LTI 1.3 Registration Status</Title>

        {clientId ? (
          <>
            <Alert
              type="success"
              showIcon
              message="Tool successfully registered!"
              style={{ marginBottom: 16 }}
              description={
                <>
                  <Paragraph>
                    Your LMS assigned the following <strong>Client ID</strong>:
                  </Paragraph>
                  <CopyableInput
                    readOnly
                    size="small"
                    value={clientId}
                    style={{ marginBottom: 16 }}
                  />
                  <Paragraph>
                    You can now deploy the tool in courses or modules.
                  </Paragraph>
                </>
              }
            />
            <div
              style={{
                display: 'flex',
                marginTop: 'auto',
                justifyContent: 'flex-end'
              }}
            >
              <Button type="primary" onClick={onClose}>
                Finish
              </Button>
            </div>
          </>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              message="Registration not found"
              description="Make sure you completed the Dynamic Registration wizard in your LMS."
            />
            <div
              style={{
                display: 'flex',
                marginTop: 'auto',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                onClick={() =>
                  setView({
                    type: 'instructions',
                    integrationId: view.integrationId,
                    url: view.url
                  })
                }
              >
                Back to instructions
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Title level={3}>LTI 1.3 Installation Instructions</Title>

      <Paragraph>
        Follow these steps to register this tool in any LTI 1.3-compatible LMS
        using <strong>Dynamic Registration</strong>.
      </Paragraph>

      <ol>
        <li>
          In your LMS, open the administrator area for
          <em> External/LTI tools</em>.
        </li>
        <li>
          Select <strong>Add Tool → Dynamic Registration</strong>.
        </li>
        <li>
          Paste the registration URL below when prompted:
          <CopyableInput readOnly size="small" value={view.url} />
        </li>
        <li>Complete the wizard and save.</li>
        <li>
          Deploy the tool to courses or modules as required (your LMS may call
          this a <em>deployment</em> or <em>placement</em>).
        </li>
      </ol>

      <Alert
        type="info"
        showIcon
        message="Platform-specific help"
        style={{ marginBottom: 16 }}
        description={
          <ul style={{ paddingInlineStart: 20, marginTop: 8 }}>
            <li>
              <Link
                href="https://community.canvaslms.com/t5/Admin-Guide/How-do-I-add-an-app-using-Dynamic-Registration-in-Canvas-Apps/ta-p/648045"
                target="_blank"
              >
                Canvas – Dynamic Registration
              </Link>
            </li>
            <li>
              <Link
                href="https://docs.moodle.org/en/LTI_External_tools#Registering_an_LTI_Advantage_tool"
                target="_blank"
              >
                Moodle – Register an LTI Advantage tool
              </Link>
            </li>
            <li>
              <Link
                href="https://help.blackboard.com/Learn/Administrator/SaaS/Integrations/Learning_Tools_Interoperability"
                target="_blank"
              >
                Blackboard – LTI 1.3 / Advantage
              </Link>
            </li>
            <li>
              <Link
                href="https://community.d2l.com/brightspace/kb/articles/23662-tool-registration-deployment-and-links"
                target="_blank"
              >
                Brightspace – Tool registration & deployment
              </Link>
            </li>
            <li>
              <Link
                href="https://sakai.screenstepslive.com/s/sakai_help/m/114555/l/1629109-how-do-i-set-up-an-lti-advantage-tool-using-dynamic-registration"
                target="_blank"
              >
                Sakai – Dynamic Registration setup
              </Link>
            </li>
          </ul>
        }
      />
      <div
        style={{
          display: 'flex',
          marginTop: 'auto',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          type="primary"
          loading={view.type === 'result' && resultIsLoading}
          onClick={() => {
            setView({
              type: 'result',
              integrationId: view.integrationId,
              url: view.url
            });
            refetch();
          }}
        >
          I’ve completed the registration
        </Button>
      </div>
    </div>
  );
}
